export namespace JSONRPC {
  export interface Dictionary<T = unknown> {
    [key: string]: T
  }

  export interface Request<TParams extends unknown[] | Dictionary<unknown> = unknown[] | Dictionary<unknown>> {
    jsonrpc: '2.0' | (string & {})
    id: string | number | null
    method: string
    params?: TParams
  }

  export interface SuccessResponse<R = unknown> {
    jsonrpc: '2.0' | (string & {})
    id: string | number | null
    result: R
  }

  export interface ErrorObject<D = unknown> {
    /**
     * Error code.
     *
     * - `-32700`: Parse error
     * - `-32600`: Invalid Request
     * - `-32601`: Method not found
     * - `-32602`: Invalid params
     * - `-32603`: Internal error
     * - `Other`: Custom error codes
     */
    code:
      | -32700 // Parse error
      | -32600 // Invalid Request
      | -32601 // Method not found
      | -32602 // Invalid params
      | -32603 // Internal error
      | (number & {})
    message: string
    data?: D
  }

  export interface ErrorResponse<E = unknown> {
    jsonrpc: '2.0' | (string & {})
    id: string | number | null
    error: ErrorObject<E>
  }

  export type Response<R = unknown, E = unknown> = SuccessResponse<R> | ErrorResponse<E>
  export type Event = Request | Response
}

export function isRequest<TParams extends unknown[] | JSONRPC.Dictionary<unknown> = unknown[] | JSONRPC.Dictionary<unknown>>(obj: unknown): obj is JSONRPC.Request<TParams> {
  if (typeof obj !== 'object' || !obj)
    return false
  if (!('jsonrpc' in obj) || !('id' in obj) || !('method' in obj) || !('params' in obj))
    return false
  return typeof obj.jsonrpc === 'string'
    && (typeof obj.method === 'string')
    && (typeof obj.params === 'object' && obj.params !== null)
}

export function isResponse<R = unknown, E = unknown>(obj: unknown): obj is JSONRPC.Response<R, E> {
  if (typeof obj !== 'object' || !obj)
    return false

  return 'jsonrpc' in obj
    && 'id' in obj
    && ('result' in obj || 'error' in obj)
}
